import { useState, type ReactNode } from 'react';
import {
  ArrowRight,
  Play,
  RefreshCw,
  Ruler,
  ShieldCheck,
  Undo2,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  Disclosure,
  Field,
  Input,
  Stat,
  cn,
} from '@/components/ui';
import {
  PLACEHOLDER,
  formatDMS,
  formatFixed,
  formatPpm,
  formatScale,
} from '@/logic/statistics';
import type { PointPair, TransformationSolution } from '@/types';
import {
  largestActiveResidual,
  solutionModeOf,
  unitsLabel,
  type NextAction,
} from './adjustmentDisplay';
import { HelpTip } from './HelpTip';
import { SigmaZero } from './Symbols';

const isValidScale = (text: string) => {
  const value = Number(text);
  return text.trim() !== '' && Number.isFinite(value) && value > 0;
};

/**
 * Mounted only while open, so each opening starts its draft from the current
 * scale setting.
 */
const ScaleDialog = ({
  onOpenChange,
  customScale,
  busy,
  onHoldScale,
  onEstimateScale,
}: {
  onOpenChange: (open: boolean) => void;
  customScale: string;
  busy: boolean;
  onHoldScale: (scale: string) => void;
  onEstimateScale: () => void;
}) => {
  const [draft, setDraft] = useState(customScale);
  const [touched, setTouched] = useState(false);
  const invalid = touched && !isValidScale(draft);
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        size="sm"
        title="Scale factor"
        description="Hold the scale at a known value, or let the fit estimate it. Either choice solves again."
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setTouched(true);
            if (!isValidScale(draft)) return;
            onHoldScale(draft.trim());
            onOpenChange(false);
          }}
        >
          <DialogBody>
            <Field
              label="Held scale factor"
              help="For example 1 for a rotation and shift only."
              error={invalid ? 'Enter a positive number.' : undefined}
            >
              <Input
                mono
                inputMode="decimal"
                aria-label="Fixed scale factor"
                placeholder="1.00000000"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={() => setTouched(true)}
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            {customScale ? (
              <Button
                onClick={() => {
                  onEstimateScale();
                  onOpenChange(false);
                }}
              >
                Estimate scale
              </Button>
            ) : (
              <Button onClick={() => onOpenChange(false)}>Cancel</Button>
            )}
            <Button type="submit" variant="primary" loading={busy}>
              Hold and re-solve
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Definition = ({
  label,
  helpLabel,
  value,
  note,
  help,
}: {
  label: ReactNode;
  /** Accessible name of the help button, e.g. "Explain rotation". */
  helpLabel?: string;
  value: ReactNode;
  note?: ReactNode;
  help?: ReactNode;
}) => (
  <div className="flex min-w-0 items-baseline justify-between gap-3 border-b border-border py-2 last:border-b-0">
    <dt className="flex items-center gap-1 text-xs text-fg-muted">
      {label}
      {help && helpLabel && <HelpTip label={helpLabel}>{help}</HelpTip>}
    </dt>
    <dd className="min-w-0 text-right">
      <span className="font-mono text-sm tabular-nums text-fg">{value}</span>
      {note && <span className="ml-2 text-xs text-fg-muted">{note}</span>}
    </dd>
  </div>
);

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <h3 className="mb-1 text-[11px] font-medium uppercase tracking-wide text-fg-muted">
    {children}
  </h3>
);

const SolutionDetails = ({
  solution,
}: {
  solution: TransformationSolution;
}) => {
  const noRedundancy = solution.redundancy <= 0;
  const statistic = (value: number | undefined, decimals = 4) =>
    noRedundancy ? PLACEHOLDER : formatFixed(value, decimals);
  return (
    <div className="grid gap-x-8 gap-y-5 md:grid-cols-2">
      <div>
        <SectionLabel>Transformation</SectionLabel>
        <dl>
          <Definition
            label="Scale factor"
            value={formatScale(solution.scale)}
            note={
              solution.scaleFixed
                ? 'held'
                : solution.sigmaScale !== undefined
                  ? `± ${formatScale(solution.sigmaScale)}`
                  : PLACEHOLDER
            }
            helpLabel="Explain scale factor"
            help="Ratio between Field and Record distances. The note is its standard deviation, or “held” when you fixed it."
          />
          <Definition
            label="Scale departure"
            value={formatPpm(solution.scale)}
          />
          <Definition
            label="Rotation"
            value={formatDMS(solution.rotation)}
            note={
              solution.sigmaRotation !== undefined
                ? `± ${formatDMS(solution.sigmaRotation)}`
                : PLACEHOLDER
            }
            helpLabel="Explain rotation"
            help="Positive rotation is clockwise, matching the equations below."
          />
          <Definition
            label="Translation east (c)"
            value={formatFixed(solution.translationE, 4)}
          />
          <Definition
            label="Translation north (d)"
            value={formatFixed(solution.translationN, 4)}
          />
        </dl>
      </div>
      <div>
        <SectionLabel>Statistics</SectionLabel>
        <dl>
          <Definition label="RMS north" value={statistic(solution.rmsN)} />
          <Definition label="RMS east" value={statistic(solution.rmsE)} />
          <Definition
            label="RMS total"
            value={statistic(solution.rmsTotal)}
            helpLabel="Explain RMS total"
            help="Ground-unit RMS of all residual components over the redundancy."
          />
          <Definition
            label={
              <>
                Unit weight <SigmaZero />
              </>
            }
            value={formatFixed(solution.sigma0, 4)}
            helpLabel="Explain the standard error of unit weight"
            help="A-posteriori standard error of unit weight. It drives the τ test, parameter deviations, and uncertainty circles; it equals RMS total when every pair in the fit is Unity."
          />
          <Definition
            label="Pairs in fit · redundancy"
            value={`${solution.pointCount} · r ${solution.redundancy}`}
            note={
              solution.parameterCount !== undefined
                ? `${solution.parameterCount} parameters`
                : undefined
            }
          />
        </dl>
      </div>
      <div className="md:col-span-2">
        <SectionLabel>Equations</SectionLabel>
        <div className="grid gap-4 rounded-lg border border-border bg-surface-sunken px-4 py-3 sm:grid-cols-[auto_1fr]">
          <div className="space-y-1 font-mono text-sm text-fg">
            <p>
              E<sub>2</sub> = a·E<sub>1</sub> + b·N<sub>1</sub> + c
            </p>
            <p>
              N<sub>2</sub> = −b·E<sub>1</sub> + a·N<sub>1</sub> + d
            </p>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-xs text-fg-muted sm:justify-self-end">
            {(
              [
                ['a', formatFixed(solution.a, 10)],
                ['b', formatFixed(solution.b, 10)],
                ['c', formatFixed(solution.c, 5)],
                ['d', formatFixed(solution.d, 5)],
              ] as const
            ).map(([name, value]) => (
              <div key={name} className="flex gap-2">
                <dt>{name} =</dt>
                <dd className="tabular-nums text-fg">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
};

export interface SolutionSummaryProps {
  solution: TransformationSolution | null;
  solutionStale: boolean;
  pointPairs: PointPair[];
  activePointCount: number;
  units: string;
  customScale: string;
  canUndoApply: boolean;
  nextAction: NextAction;
  /** The detection results exist but are outdated (labels "Re-run check"). */
  detectionStale: boolean;
  /** Another surface (trial, verdict) holds the screen's primary action. */
  deferPrimary: boolean;
  busy: boolean;
  onSolve: () => void;
  onCheckBlunders: () => void;
  onContinueToExport: () => void;
  onUndoApply: () => void;
  onHoldScale: (scale: string) => void;
  onEstimateScale: () => void;
  onSelectPoint: (id: number) => void;
}

/**
 * Solution header for Adjust: status, KPIs, the next action, and the
 * parameters behind a disclosure.
 */
export const SolutionSummary = ({
  solution,
  solutionStale,
  pointPairs,
  activePointCount,
  units,
  customScale,
  canUndoApply,
  nextAction,
  detectionStale,
  deferPrimary,
  busy,
  onSolve,
  onCheckBlunders,
  onContinueToExport,
  onUndoApply,
  onHoldScale,
  onEstimateScale,
  onSelectPoint,
}: SolutionSummaryProps) => {
  const [scaleOpen, setScaleOpen] = useState(false);
  const hasSolution = !!solution && !solution.error;
  const fit = hasSolution ? solution : null;
  const noRedundancy = !!fit && fit.redundancy <= 0;
  const largest = largestActiveResidual(fit);
  const largestPair = pointPairs.find((pair) => pair.id === largest?.id);
  const unitLabel = unitsLabel(units);
  const mode = fit ? solutionModeOf(fit) : null;
  const scaleHeld = customScale.trim() !== '';

  const tooFew = activePointCount < 2;
  const primaryVariant = deferPrimary ? 'secondary' : 'primary';
  const primary = (() => {
    switch (nextAction) {
      case 'solve':
      case 'resolve':
        return (
          <Button
            variant={tooFew ? 'secondary' : primaryVariant}
            className="max-sm:order-first max-sm:w-full"
            leadingIcon={nextAction === 'solve' ? <Play /> : <RefreshCw />}
            loading={busy && !tooFew}
            disabled={tooFew}
            disabledReason={
              tooFew
                ? 'Needs at least two complete pairs weighted Unity or Fixed'
                : undefined
            }
            onClick={onSolve}
          >
            {nextAction === 'solve' ? 'Solve' : 'Re-solve'}
          </Button>
        );
      case 'check':
        return (
          <Button
            variant={primaryVariant}
            className="max-sm:order-first max-sm:w-full"
            leadingIcon={<ShieldCheck />}
            loading={busy}
            onClick={onCheckBlunders}
          >
            {detectionStale ? 'Re-run check' : 'Check for blunders'}
          </Button>
        );
      case 'export':
        return (
          <Button
            variant={primaryVariant}
            className="max-sm:order-first max-sm:w-full"
            trailingIcon={<ArrowRight />}
            onClick={onContinueToExport}
          >
            Continue to export
          </Button>
        );
    }
  })();

  const reason = (text: string) => (
    <span className="text-warning-fg">{text}</span>
  );
  const noRedundancyNote = reason('No redundancy');
  const notSolved = 'Solve to compute';

  return (
    <Card
      role="region"
      aria-label="Adjustment solution"
      className="flex min-w-0 flex-col lg:self-stretch"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 pt-4 sm:px-5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold text-fg">Solution</h2>
          {!solution ? (
            <Badge>Not solved</Badge>
          ) : solution.error ? (
            <Badge tone="danger" dot>
              Failed
            </Badge>
          ) : solutionStale ? (
            <Badge tone="warning" dot>
              Outdated
            </Badge>
          ) : (
            <Badge tone="success" dot>
              Current
            </Badge>
          )}
          {mode && (
            <span className="inline-flex items-center gap-0.5">
              <Badge>{mode.label}</Badge>
              <HelpTip label="Explain solution mode">{mode.help}</HelpTip>
            </span>
          )}
          {unitLabel && <Badge tone="accent">Units · {unitLabel}</Badge>}
        </div>
        <div className="flex w-full flex-wrap items-center gap-1.5 sm:ml-auto sm:w-auto sm:justify-end">
          {canUndoApply && (
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Undo2 />}
              onClick={onUndoApply}
            >
              Undo last apply
            </Button>
          )}
          {(hasSolution || scaleHeld) && (
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Ruler />}
              aria-pressed={scaleHeld}
              onClick={() => setScaleOpen(true)}
            >
              {scaleHeld ? `Scale held · ${customScale}` : 'Scale estimated'}
            </Button>
          )}
          {nextAction === 'check' && (
            <Button variant="ghost" size="sm" onClick={onContinueToExport}>
              Continue to export
            </Button>
          )}
          {primary}
        </div>
      </div>

      <div className="flex flex-1 flex-col px-4 py-5 sm:px-5">
        {solution?.error ? (
          <Alert tone="danger" title="The fit could not be solved">
            {solution.error}
          </Alert>
        ) : (
          <div
            aria-label="Solution summary"
            className="grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4"
          >
            <Stat
              label="Pairs in fit"
              value={String(fit ? fit.pointCount : activePointCount)}
              sublabel={
                fit
                  ? noRedundancy
                    ? reason('Redundancy 0 · no check')
                    : `Redundancy ${fit.redundancy}`
                  : `${pointPairs.length} pairs total`
              }
            />
            <Stat
              label="RMS"
              value={
                fit && !noRedundancy
                  ? formatFixed(fit.rmsTotal, 4)
                  : PLACEHOLDER
              }
              sublabel={
                !fit
                  ? notSolved
                  : noRedundancy
                    ? noRedundancyNote
                    : 'All components'
              }
            />
            <Stat
              label="Largest residual"
              value={
                fit && !noRedundancy
                  ? formatFixed(largest?.total, 4)
                  : PLACEHOLDER
              }
              sublabel={
                !fit ? (
                  notSolved
                ) : noRedundancy ? (
                  noRedundancyNote
                ) : largestPair ? (
                  <button
                    type="button"
                    onClick={() => onSelectPoint(largestPair.id)}
                    className="max-w-full truncate rounded text-left hover:text-fg hover:underline"
                    aria-label={`Show pair ${largestPair.label} with the largest residual`}
                  >
                    Record {largestPair.sys1Point} → Field{' '}
                    {largestPair.sys2Point}
                  </button>
                ) : (
                  PLACEHOLDER
                )
              }
            />
            <Stat
              label={<SigmaZero />}
              value={fit ? formatFixed(fit.sigma0, 4) : PLACEHOLDER}
              sublabel={
                !fit
                  ? notSolved
                  : fit.sigma0 === undefined
                    ? noRedundancyNote
                    : 'Std. error of unit weight'
              }
            />
          </div>
        )}
      </div>

      {fit && (
        <div className="mt-auto border-t border-border px-2 py-1 sm:px-3">
          <Disclosure
            variant="plain"
            title="Parameters, statistics, and equations"
            meta={
              <span
                className={cn(
                  'hidden sm:inline',
                  solutionStale && 'text-warning-fg'
                )}
              >
                {solutionStale
                  ? 'Previous inputs'
                  : `Rotation ${formatDMS(fit.rotation)} · scale ${formatScale(fit.scale, 6)}`}
              </span>
            }
            className="[&>button]:mx-0 [&>button]:px-2"
            panelClassName="px-2 pb-3"
          >
            <SolutionDetails solution={fit} />
          </Disclosure>
        </div>
      )}
      {scaleOpen && (
        <ScaleDialog
          onOpenChange={setScaleOpen}
          customScale={customScale}
          busy={busy}
          onHoldScale={onHoldScale}
          onEstimateScale={onEstimateScale}
        />
      )}
    </Card>
  );
};
